/**
 * MobileWeekCard Component
 *
 * Compact card component for displaying one week of timeline data on mobile.
 * Shows demand vs supply with simple horizontal progress bars and gap indicator.
 *
 * US-PE-006: Sales Timeline - Phase 4 Mobile View
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/**
 * Calculate the total demand and supply for a week
 * @param {Array} weekData - Array of daily data objects
 * @returns {Object} { totalDemand, totalSupply }
 */
function calculateWeekTotals(weekData) {
  return weekData.reduce(
    (acc, day) => ({
      totalDemand: acc.totalDemand + (day.demandKg || 0),
      totalSupply: acc.totalSupply + (day.totalSupplyKg || 0)
    }),
    { totalDemand: 0, totalSupply: 0 }
  )
}

/**
 * Calculate the gap between supply and demand
 * @param {number} supply - Total supply in kg
 * @param {number} demand - Total demand in kg
 * @returns {Object} { value, type } where type is 'surplus', 'shortfall', or 'balanced'
 */
function calculateGap(supply, demand) {
  const gap = supply - demand
  const threshold = 0.5 // Consider balanced if within 0.5kg

  if (Math.abs(gap) < threshold) {
    return { value: 0, type: 'balanced' }
  }

  return {
    value: gap,
    type: gap > 0 ? 'surplus' : 'shortfall'
  }
}

/**
 * Progress bar component for demand/supply visualisation
 */
function ProgressBar({ label, value, maxValue, colorClass }) {
  const percentage = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value.toFixed(1)} kg</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', colorClass)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}

/**
 * Gap indicator component with semantic colouring
 */
function GapIndicator({ gap }) {
  const { value, type } = gap

  const config = {
    surplus: {
      bgClass: 'bg-emerald-50 dark:bg-emerald-950/30',
      textClass: 'text-emerald-700 dark:text-emerald-400',
      label: 'surplus'
    },
    shortfall: {
      bgClass: 'bg-red-50 dark:bg-red-950/30',
      textClass: 'text-red-700 dark:text-red-400',
      label: 'shortfall'
    },
    balanced: {
      bgClass: 'bg-slate-50 dark:bg-slate-800/50',
      textClass: 'text-slate-600 dark:text-slate-400',
      label: 'balanced'
    }
  }

  const { bgClass, textClass, label } = config[type]
  const displayValue = type === 'balanced'
    ? 'Balanced'
    : `${value > 0 ? '+' : ''}${value.toFixed(1)} kg (${label})`

  return (
    <div className={cn('px-3 py-2 rounded-lg text-sm font-medium text-center', bgClass, textClass)}>
      Gap: {displayValue}
    </div>
  )
}

/**
 * MobileWeekCard - Compact card for one week of timeline data
 *
 * @param {Object} props
 * @param {Array} props.weekData - Array of 7 day objects with date, demandKg, totalSupplyKg
 * @param {string} props.weekLabel - Week label (e.g., "6 - 12 Jan")
 * @param {Array} [props.strains] - Array of strain objects (for future colour reference)
 * @param {boolean} [props.showAllStrains] - Whether showing all strains (for future use)
 * @param {string} [props.className] - Additional CSS classes
 */
export default function MobileWeekCard({
  weekData = [],
  weekLabel,
  strains = [], // eslint-disable-line no-unused-vars -- Reserved for future per-strain breakdown
  showAllStrains = true, // eslint-disable-line no-unused-vars -- Reserved for future per-strain breakdown
  className = ''
}) {
  // Calculate totals for the week
  const { totalDemand, totalSupply } = calculateWeekTotals(weekData)

  // Calculate gap
  const gap = calculateGap(totalSupply, totalDemand)

  // Max value for progress bars (use the larger of demand or supply)
  const maxValue = Math.max(totalDemand, totalSupply, 1)

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">
          {weekLabel || 'Week'}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress bars */}
        <div className="space-y-3">
          <ProgressBar
            label="Demand"
            value={totalDemand}
            maxValue={maxValue}
            colorClass="bg-amber-500"
          />
          <ProgressBar
            label="Supply"
            value={totalSupply}
            maxValue={maxValue}
            colorClass="bg-emerald-500"
          />
        </div>

        {/* Gap indicator */}
        <GapIndicator gap={gap} />
      </CardContent>
    </Card>
  )
}
